import { Type, Static } from '@sinclair/typebox'

import { OrganisationPublicSummaryTB } from './organisation'
import { VisionSharingScopesTB } from './vision'

const LinkedFarmInvitationStatusTB = Type.Union([
  Type.Literal('invited'),
  Type.Literal('awaiting_vision_org'),
  Type.Literal('archived'),
])

export const LinkedFarmInvitationRecordTB = Type.Object({
  id: Type.String(),
  deletedAt: Type.Union([Type.Null(), Type.String()]),
  email: Type.String(),
  visionOrgId: Type.String(),
  created: Type.Union([Type.Null(), Type.String()]),
  updated: Type.Union([Type.Null(), Type.String()]),
  clientReferenceId: Type.Union([Type.Null(), Type.String()]),
  recipientOrgId: Type.Union([Type.Null(), Type.String()]),
  status: LinkedFarmInvitationStatusTB,
  requestedScopes: Type.Array(VisionSharingScopesTB),
})
export type LinkedFarmInvitationRecordT = Static<typeof LinkedFarmInvitationRecordTB>

export const LinkedFarmInvitationCreateRecordTB = Type.Composite([
  Type.Omit(LinkedFarmInvitationRecordTB, [
    'id',
    'deletedAt',
    'created',
    'updated',
    'clientReferenceId',
    'recipientOrgId',
    'status',
  ]),
  Type.Object({
    deletedAt: Type.Optional(Type.String()),
    created: Type.Optional(Type.String()),
    updated: Type.Optional(Type.String()),
    clientReferenceId: Type.Optional(Type.String()),
    recipientOrgId: Type.Optional(Type.String()),
    status: Type.Optional(LinkedFarmInvitationStatusTB),
  }),
])
export type LinkedFarmInvitationCreateRecordT = Static<typeof LinkedFarmInvitationCreateRecordTB>

export const LinkedFarmInvitationUpdateRecordTB = Type.Partial(
  Type.Omit(LinkedFarmInvitationRecordTB, ['id']),
)
export type LinkedFarmInvitationUpdateRecordT = Static<typeof LinkedFarmInvitationUpdateRecordTB>

export const LinkedFarmInvitationTB = Type.Composite([
  Type.Omit(LinkedFarmInvitationRecordTB, ['created', 'fromOtherOrganisations', 'updated']),
  Type.Object({
    created: Type.String(),
  }),
])
export type LinkedFarmInvitationT = Static<typeof LinkedFarmInvitationTB>

export const LinkedFarmInvitationCreateTB = Type.Composite([
  Type.Pick(LinkedFarmInvitationCreateRecordTB, [
    'email',
    'visionOrgId',
    'clientReferenceId',
    'requestedScopes',
  ]),
  // N.B. no auto-created date in the db table currently, so must provide created
  Type.Object({
    created: Type.String(),
  }),
])
export type LinkedFarmInvitationCreateT = Static<typeof LinkedFarmInvitationCreateTB>

export const LinkedFarmInvitationRecipientCreateTB = Type.Pick(LinkedFarmInvitationCreateRecordTB, [
  'email',
  'clientReferenceId',
  'requestedScopes',
])
export type LinkedFarmInvitationRecipientCreateT = Static<
  typeof LinkedFarmInvitationRecipientCreateTB
>

// N.B. in practice, we only allow creating invitations in arrays via client-sent DTO
export const LinkedFarmInvitationsCreateDTOTB = Type.Object({
  sendInviteEmail: Type.Boolean(),
  visionOrgId: Type.String(),
  recipients: Type.Array(LinkedFarmInvitationRecipientCreateTB),
})
export type LinkedFarmInvitationsCreateDTOT = Static<typeof LinkedFarmInvitationsCreateDTOTB>

export const CreateDraftVisionInvitationsFromFarmOrgTB = Type.Object({
  visionOrgId: Type.String(),
})
export type CreateDraftVisionInvitationsFromFarmOrgT = Static<
  typeof CreateDraftVisionInvitationsFromFarmOrgTB
>

export const LinkedFarmInvitationUpdateTB = Type.Partial(
  Type.Pick(LinkedFarmInvitationCreateRecordTB, ['clientReferenceId', 'status', 'requestedScopes']),
)
export type LinkedFarmInvitationUpdateT = Static<typeof LinkedFarmInvitationUpdateTB>

export const InviteStatusTypeInvitedTB = Type.Literal('invited')
export const InviteStatusTypeAwaitingVisionOrgTB = Type.Literal('awaiting_vision_org')
export const InviteStatusTypeArchivedTB = Type.Literal('archived')

export const InviteStatusTypeTB = Type.Union(
  [InviteStatusTypeInvitedTB, InviteStatusTypeAwaitingVisionOrgTB, InviteStatusTypeArchivedTB],
  { default: InviteStatusTypeInvitedTB },
)
export type InviteStatusTypeT = Static<typeof InviteStatusTypeTB>

export const LinkedFarmInvitationRecievedTB = Type.Object({
  invite: LinkedFarmInvitationTB,
  recipientDetails: Type.Object({
    email: Type.String(),
  }),
  visionOrganisation: OrganisationPublicSummaryTB,
})
export type LinkedFarmInvitationRecievedT = Static<typeof LinkedFarmInvitationRecievedTB>

export const CreateLinkedFarmInvitationRequestTB = Type.Object({
  visionOrgId: Type.String(),
})
export type CreateLinkedFarmInvitationRequestT = Static<typeof CreateLinkedFarmInvitationRequestTB>

export const LinkedFarmInvitationSentTB = Type.Object({
  invite: LinkedFarmInvitationTB,
  recipientDetails: Type.Object({
    id: Type.Optional(Type.String()),
    firstName: Type.Optional(Type.String()),
    lastName: Type.Optional(Type.String()),
    email: Type.String(),
  }),
  primaryOrganisation: Type.Optional(OrganisationPublicSummaryTB),
})
export type LinkedFarmInvitationSentT = Static<typeof LinkedFarmInvitationSentTB>
